import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent
  from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import ability from '@/libs/acl/ability'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'

export default function useList() {
  const refListTable = ref(null)

  const toast = useToast()

  // Initialize store data
  store.dispatch('unit/getAll', { with: 'router:id,id_tenant' });
  store.dispatch('center/getAll');
  store.dispatch('sector/getAll');
  store.dispatch('omniview_reseller/getAll');

  const facilitiesOptions = computed(() => store.getters['unit/getSelectedItems']);
  const centerOptions = computed(() => store.getters['center/getSelectedItems']);
  const sectorOptions = computed(() => store.getters['sector/getSelectedItems']);
  const resellerOptions = computed(() => store.getters['omniview_reseller/getSelectedItems'])

  const facilitiesFilter = ref(null);
  const centerFilter = ref(null);
  const sectorFilter = ref(null);
  const resellerFilter = ref(null);

  // Table configuration
  const tableColumns = [
    { label: '', key: 'panel_status' },
    // { label: '', key: 'active' },
    { label: 'bosch.name', key: 'name', sortable: true },
    { label: 'bosch.panelHost', key: 'panel_host', sortable: true },
    { label: 'bosch.panelPasscode', key: 'panel_passcode', sortable: true },
    { label: 'bosch.units', key: 'units', sortable: false },
    { label: 'bosch.center', key: 'center', sortable: false },
    { label: 'bosch.sector', key: 'sector', sortable: false },
    // { label: 'bosch.reseller', key: 'id_omniview_reseller', sortable: false },
  ];

  // Add actions column if user has permissions
  if (ability.can('update', 'BoschPanel') || ability.can('delete', 'BoschPanel')) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' });
  }

  // Pagination configuration
  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('id');
  const isSortDirDesc = ref(true);

  // Data meta for pagination display
  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  // Fetch data from store
  const fetchList = (ctx, callback) => {
    store.dispatch('bosch_panels/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      center: centerFilter.value,
      sector: sectorFilter.value,
      units: facilitiesFilter.value,
      id_bosch_reseller: resellerFilter.value
    })
      .then(response => {
        callback(response.data);
        totalList.value = response.meta ? response.meta.total : response.data.length;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  // Refresh table data
  const refetchData = () => {
    if (refListTable.value) {
      refListTable.value.refresh();
    }
  };

  // Watch for pagination changes
  watch([currentPage, perPage], () => {
    refetchData();
  });

  // Watch for search query changes with debounce
  watch([searchQuery], debounce(() => {
    refetchData();
  }, 500));

  // Filters counter functionality
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);

  const buildFilters = () => {
    filters.value = {
      q: searchQuery.value,
      center: centerFilter.value,
      sector: sectorFilter.value,
      units: facilitiesFilter.value,
      id_bosch_reseller: resellerFilter.value
    };
    refetchData();
  };

  const clearSearchFilters = () => {
    searchQuery.value = null;
    centerFilter.value = null;
    sectorFilter.value = null;
    facilitiesFilter.value = null;
    resellerFilter.value = null;

    buildFilters();
  };

  return {
    // Table configuration
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,

    // Filters
    centerFilter,
    sectorFilter,
    facilitiesFilter,
    resellerFilter,

    // Options for dropdowns
    facilitiesOptions,
    centerOptions,
    sectorOptions,
    resellerOptions,

    // Methods
    refetchData,
    filtersCount,
    buildFilters,
    clearSearchFilters,
  };
}
